// Functional self-test notification endpoint (Ticket #5)
// Replaces the placeholder stub from Ticket #4.
// Requests the backend Edge Function `send-test-notification` to dispatch
// a real Web Push notification to this device's subscription.

export type TestNotificationResult =
  | { ok: true; delivered: true; note?: string }
  | { ok: true; delivered: false; note: 'local_preview' | 'stub' }
  | { ok: false; reason: string };

/**
 * Sends a test notification to the current device.
 * Queries the active PushSubscription from the service worker and calls
 * the Supabase Edge Function `send-test-notification`.
 */
export async function requestTestNotification(): Promise<TestNotificationResult> {
  if (typeof window === 'undefined') {
    return { ok: false, reason: 'window_unavailable' };
  }

  if (!('serviceWorker' in navigator) || !('PushManager' in window)) {
    return { ok: false, reason: 'Push notifications are not supported on this browser' };
  }

  try {
    const reg = await navigator.serviceWorker.ready;
    const sub = await reg.pushManager.getSubscription();

    if (!sub) {
      return {
        ok: false,
        reason: 'No active push subscription found on this device. Please turn on notifications first.',
      };
    }

    // Safely check environment variables
    const supabaseUrl = (import.meta as any).env?.VITE_SUPABASE_URL as string | undefined;
    const anonKey = (import.meta as any).env?.VITE_SUPABASE_ANON_KEY as string | undefined;

    // Check for auth access token from Supabase session if available
    let accessToken: string | undefined;
    try {
      const { getSupabase } = await import('../lib/supabaseClient');
      const sb = getSupabase();
      if (sb) {
        const { data } = await sb.auth.getSession();
        accessToken = data.session?.access_token ?? undefined;
      }
    } catch {
      // ignore
    }

    // If Supabase backend is configured, call the send-test-notification edge function
    if (supabaseUrl && anonKey) {
      const headers: Record<string, string> = {
        'Content-Type': 'application/json',
        apikey: anonKey,
      };
      if (accessToken) {
        headers['Authorization'] = `Bearer ${accessToken}`;
      }

      const functionUrl = `${supabaseUrl.replace(/\/+$/, '')}/functions/v1/send-test-notification`;
      const res = await fetch(functionUrl, {
        method: 'POST',
        headers,
        body: JSON.stringify({
          endpoint: sub.endpoint,
        }),
      });

      if (!res.ok) {
        const errText = await res.text().catch(() => '');
        return {
          ok: false,
          reason: errText || `Push service returned ${res.status}`,
        };
      }

      const data = await res.json().catch(() => ({}));
      return {
        ok: true,
        delivered: true,
        note: data.message || 'Test notification sent via Edge Function',
      };
    }

    // Fallback for preview/mock environments: display a local notification if permission is granted
    if (Notification.permission === 'granted') {
      await reg.showNotification('Shores Park Club', {
        body: 'Test notification from Shores Park Club (local preview)',
        icon: '/icons/icon-192.png',
        badge: '/icons/icon-192.png',
        tag: 'test-notification',
        data: { url: '/', type: 'test' },
      });
      return { ok: true, delivered: false, note: 'local_preview' };
    }

    return { ok: false, reason: 'Notifications not permitted' };
  } catch (err: unknown) {
    return {
      ok: false,
      reason: err instanceof Error ? err.message : 'Failed to request test notification',
    };
  }
}
